/**
 * `selectionSetWire.ts` — conversions between the web `SelectionSetData`
 * (Dynamo row shape, PascalCase) and Unity's JsonUtility event shape for
 * `update_selectionset` (tc 56, `SelectionSetEventData`).
 *
 * Event wire (`Models/Data/SelectionSetData.cs` + `SelectionSetEventData.cs`,
 * verified against the `[SerializeField]` names):
 *  - `data`: `{ name, id, pos, rot, scale, pivot, pivotR, pivotS,
 *    initialpos, initialrot, initialscale, order, entitySnapshotIDs,
 *    mediaTileIDs, visible, forcedVisible }` — vectors as `[{name,val}]`.
 *  - `action`: **int** `0=Create / 1=Update / 2=Delete` (a string here is
 *    silently ignored by JsonUtility and decodes as Create — the exact bug
 *    the web used to have).
 *  - `flags`: **int** mask `Name=1 | Pose=2 | Entities=4 | Visibility=8`.
 */

import { toNameFloatMap } from "./nameFloatMap";
import type { SelectionSetData, SelectionSetUpdateFlag } from "./selectionSetData";

export const SELECTION_SET_ACTION = Object.freeze({
  Create: 0,
  Update: 1,
  Delete: 2,
} as const);

export type SelectionSetAction = "Create" | "Update" | "Delete";

export function selectionSetActionToWire(action: SelectionSetAction): number {
  return SELECTION_SET_ACTION[action];
}

/** Tolerates both the int wire value and the legacy web string. */
export function selectionSetActionFromWire(value: unknown): SelectionSetAction | null {
  if (typeof value === "number") {
    if (value === 0) return "Create";
    if (value === 1) return "Update";
    if (value === 2) return "Delete";
    return null;
  }
  if (typeof value === "string") {
    const lower = value.toLowerCase();
    if (lower === "create") return "Create";
    if (lower === "update") return "Update";
    if (lower === "delete") return "Delete";
  }
  return null;
}

export function selectionSetFlagMask(
  flags: ReadonlyArray<SelectionSetUpdateFlag>,
): number {
  let mask = 0;
  if (flags.includes("Name")) mask |= 1 << 0;
  if (flags.includes("Pose")) mask |= 1 << 1;
  if (flags.includes("Entities")) mask |= 1 << 2;
  if (flags.includes("Visibility")) mask |= 1 << 3;
  return mask;
}

type WirePair = { readonly name: string; readonly val: number };

/**
 * Pose emission must ALWAYS carry all three axes: Unity rebuilds
 * `Dictionary<string,float>`s from the pair arrays and a missing/empty
 * pose collapses to defaults — an empty `scale` array spawns the
 * selection-set container at localScale (0,0,0) on Unity peers.
 */
function poseToPairs(
  value: unknown,
  keys: ReadonlyArray<string>,
  fallback: number,
): WirePair[] {
  const map = toNameFloatMap(value);
  return keys.map((key) => ({
    name: key,
    val: typeof map[key] === "number" ? (map[key] as number) : fallback,
  }));
}

function pairsToRecord(value: unknown): Record<string, number> | undefined {
  if (value === undefined || value === null) return undefined;
  return { ...toNameFloatMap(value) };
}

const XYZ = ["x", "y", "z"] as const;

/** Web (Dynamo-shaped) selection set → JsonUtility event `data` object. */
export function selectionSetToWire(set: SelectionSetData): Record<string, unknown> {
  return {
    name: set.Name ?? "",
    id: set.ID,
    // The web never authors the runtime pose fields (`Position`/`Pivot`/…) —
    // pass them through when present, default-filling missing axes with
    // Unity's constructor defaults (scales 1, everything else 0).
    pos: poseToPairs(set.Position, XYZ, 0),
    rot: poseToPairs(set.Rotation, XYZ, 0),
    scale: poseToPairs(set.Scale, XYZ, 1),
    pivot: poseToPairs(set.Pivot, XYZ, 0),
    pivotR: poseToPairs(set.PivotR, XYZ, 0),
    pivotS: poseToPairs(set.PivotS, XYZ, 1),
    initialpos: poseToPairs(set.IntPosition, XYZ, 0),
    initialrot: poseToPairs(set.IntRotation, XYZ, 0),
    initialscale: poseToPairs(set.IntScale, XYZ, 1),
    order: set.DisplayOrder ?? 0,
    entitySnapshotIDs: [...(set.EntitySnapshotIDs ?? [])],
    mediaTileIDs: [...(set.MediaTileIDs ?? [])],
    visible: set.IsVisible ?? true,
    forcedVisible: set.IsForcedVisible ?? true,
  };
}

/**
 * Inbound event `data` → web `SelectionSetData`. Tolerates BOTH the Unity
 * JsonUtility shape and the legacy web Dynamo shape (older web builds
 * published PascalCase payloads).
 */
export function selectionSetFromWire(
  wire: Record<string, unknown>,
): SelectionSetData | null {
  const id = strField(wire, "id", "ID");
  if (!id) return null;
  return {
    ID: id,
    Name: strField(wire, "name", "Name") ?? "",
    DisplayOrder: numField(wire, "order", "DisplayOrder") ?? 0,
    EntitySnapshotIDs: stringArray(wire.entitySnapshotIDs ?? wire.EntitySnapshotIDs),
    MediaTileIDs: stringArray(wire.mediaTileIDs ?? wire.MediaTileIDs),
    IsVisible: boolField(wire, "visible", "IsVisible") ?? true,
    IsForcedVisible: boolField(wire, "forcedVisible", "IsForcedVisible") ?? true,
    Position: pairsToRecord(wire.pos ?? wire.Position),
    Rotation: pairsToRecord(wire.rot ?? wire.Rotation),
    Scale: pairsToRecord(wire.scale ?? wire.Scale),
    Pivot: pairsToRecord(wire.pivot ?? wire.Pivot),
    PivotR: pairsToRecord(wire.pivotR ?? wire.PivotR),
    PivotS: pairsToRecord(wire.pivotS ?? wire.PivotS),
    IntPosition: pairsToRecord(wire.initialpos ?? wire.IntPosition),
    IntRotation: pairsToRecord(wire.initialrot ?? wire.IntRotation),
    IntScale: pairsToRecord(wire.initialscale ?? wire.IntScale),
  };
}

function strField(value: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const field = value[key];
    if (typeof field === "string" && field.length > 0) return field;
  }
  return null;
}

function numField(value: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const field = value[key];
    if (typeof field === "number" && Number.isFinite(field)) return field;
  }
  return null;
}

function boolField(value: Record<string, unknown>, ...keys: string[]): boolean | null {
  for (const key of keys) {
    const field = value[key];
    if (typeof field === "boolean") return field;
  }
  return null;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}
